import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  FileText, Plus, DollarSign, Clock, CheckCircle, 
  AlertCircle, Download, Edit, Trash2, Eye, Search,
  Settings, BarChart3, Filter, Users, Send, Upload, Zap, Bell
} from 'lucide-react';

// Template definitions with preview colors
const TEMPLATES = [
  { id: 'modern', name: 'Modern', color: '#4F46E5', description: 'Clean indigo style' },
  { id: 'classic', name: 'Classic', color: '#1F2937', description: 'Traditional black & white' },
  { id: 'professional', name: 'Professional', color: '#0F766E', description: 'Teal business look' },
  { id: 'minimal', name: 'Minimal', color: '#000000', description: 'Simple and clean' },
  { id: 'corporate', name: 'Corporate', color: '#1E40AF', description: 'Blue corporate style' },
  { id: 'creative', name: 'Creative', color: '#7C3AED', description: 'Purple creative style' },
  { id: 'elegant', name: 'Elegant', color: '#78350F', description: 'Golden elegant look' },
  { id: 'bold', name: 'Bold', color: '#DC2626', description: 'Red bold statements' },
  { id: 'nature', name: 'Nature', color: '#166534', description: 'Green natural feel' },
  { id: 'ocean', name: 'Ocean', color: '#0369A1', description: 'Blue ocean style' }
];

const Invoices = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list'); // list, create, awaiting, reports, settings
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userSubscription, setUserSubscription] = useState(null);
  
  // Invoice limit for free users
  const FREE_INVOICE_LIMIT = 20;
  
  // Reports state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    start_date: '',
    end_date: '',
    client_id: '',
    service: '',
    status: 'all'
  });
  const [availableServices, setAvailableServices] = useState([]);
  
  // Create invoice form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed');
  
  // Client form state
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '', email: '', address: '', city: '', state: '', zip_code: '', phone: ''
  });
  
  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    company_name: '', company_address: '', company_city: '', company_state: '',
    company_zip: '', company_phone: '', company_email: '', default_tax_rate: 0,
    default_payment_terms: 30, invoice_prefix: 'INV', logo_url: ''
  });

  // Work suggestions for Smart Price
  const [workSuggestions, setWorkSuggestions] = useState([]);
  const [smartPriceEnabled, setSmartPriceEnabled] = useState(true);
  
  // Edit client state
  const [editingClient, setEditingClient] = useState(null);
  
  // Edit invoice state
  const [editingInvoice, setEditingInvoice] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Set default due date based on payment terms
    if (settings?.default_payment_terms) {
      const due = new Date();
      due.setDate(due.getDate() + settings.default_payment_terms);
      setDueDate(due.toISOString().split('T')[0]);
    }
    if (settings?.default_tax_rate) {
      setTaxRate(settings.default_tax_rate);
    }
  }, [settings]);

  useEffect(() => {
    // Fetch report data when switching to reports tab
    if (activeTab === 'reports') {
      fetchReportData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, clientsRes, statsRes, settingsRes, userRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/invoices/clients'),
        api.get('/invoices/stats'),
        api.get('/invoices/settings'),
        api.get('/auth/me')
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setUserSubscription(userRes.data?.subscription_status);
      if (settingsRes.data) {
        setSettingsForm({
          company_name: settingsRes.data.company_name || '',
          company_address: settingsRes.data.company_address || '',
          company_city: settingsRes.data.company_city || '',
          company_state: settingsRes.data.company_state || '',
          company_zip: settingsRes.data.company_zip || '',
          company_phone: settingsRes.data.company_phone || '',
          company_email: settingsRes.data.company_email || '',
          default_tax_rate: settingsRes.data.default_tax_rate || 0,
          default_payment_terms: settingsRes.data.default_payment_terms || 30,
          invoice_prefix: settingsRes.data.invoice_prefix || 'INV'
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    setReportLoading(true);
    try {
      const [reportRes, servicesRes] = await Promise.all([
        api.post('/invoices/reports', reportFilters),
        api.get('/invoices/available-services')
      ]);
      setReportData(reportRes.data);
      setAvailableServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchWorkSuggestions = async (query) => {
    if (!smartPriceEnabled || query.length < 2) {
      setWorkSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/invoices/work-history/suggestions?q=${encodeURIComponent(query)}`);
      setWorkSuggestions(res.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const discount = discountType === 'percentage' ? subtotal * (discountAmount / 100) : discountAmount;
    const taxable = subtotal - discount;
    const tax = taxable * (taxRate / 100);
    const total = taxable + tax;
    return { subtotal, discount, tax, total };
  };

  const handleCreateInvoice = async () => {
    // Check invoice limit for free users
    if (userSubscription !== 'active' && stats && stats.total_invoices >= FREE_INVOICE_LIMIT) {
      toast.error(`Free plan limit reached (${FREE_INVOICE_LIMIT} invoices). Please upgrade to Premium.`);
      navigate('/subscription');
      return;
    }
    
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (items.length === 0 || !items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const invoiceData = {
        client_id: selectedClient,
        template_id: selectedTemplate,
        issue_date: issueDate,
        due_date: dueDate,
        items: items.filter(i => i.description),
        notes,
        tax_rate: taxRate,
        discount_amount: discountAmount,
        discount_type: discountType
      };

      await api.post('/invoices', invoiceData);
      toast.success('Invoice created successfully');
      setShowCreateForm(false);
      resetForm();
      fetchData();
      setActiveTab('list');
    } catch (error) {
      console.error('Failed to create invoice:', error);
      // Handle specific error for limit reached
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Invoice limit reached. Please upgrade to Premium.');
        navigate('/subscription');
      } else {
        toast.error('Failed to create invoice');
      }
    }
  };

  const resetForm = () => {
    setSelectedClient('');
    setSelectedTemplate('modern');
    setIssueDate(new Date().toISOString().split('T')[0]);
    setItems([{ description: '', quantity: 1, unit_price: 0 }]);
    setNotes('');
    setTaxRate(settings?.default_tax_rate || 0);
    setDiscountAmount(0);
    setDiscountType('fixed');
  };

  const handleCreateClient = async () => {
    if (!clientForm.name) {
      toast.error('Client name is required');
      return;
    }

    try {
      if (editingClient) {
        // Update existing client
        const res = await api.put(`/invoices/clients/${editingClient.id}`, clientForm);
        setClients(clients.map(c => c.id === editingClient.id ? res.data : c));
        toast.success('Client updated');
      } else {
        // Create new client
        const res = await api.post('/invoices/clients', clientForm);
        setClients([...clients, res.data]);
        setSelectedClient(res.data.id);
        toast.success('Client created');
      }
      setShowClientForm(false);
      setEditingClient(null);
      setClientForm({ name: '', email: '', address: '', city: '', state: '', zip_code: '', phone: '' });
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await api.delete(`/invoices/clients/${clientId}`);
      setClients(clients.filter(c => c.id !== clientId));
      toast.success('Client deleted');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/invoices/settings', settingsForm);
      toast.success('Settings saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/mark-paid`);
      toast.success('Invoice marked as paid and added to income');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setSelectedClient(invoice.client_id);
    setSelectedTemplate(invoice.template_id);
    setIssueDate(invoice.issue_date);
    setDueDate(invoice.due_date);
    setItems(invoice.items.map(item => ({
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price
    })));
    setNotes(invoice.notes || '');
    setTaxRate(invoice.tax_rate || 0);
    setDiscountAmount(invoice.discount_amount || 0);
    setDiscountType(invoice.discount_type || 'fixed');
    setShowCreateForm(true);
    setActiveTab('create');
  };

  const handleUpdateInvoice = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (items.length === 0 || !items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const invoiceData = {
        client_id: selectedClient,
        template_id: selectedTemplate,
        issue_date: issueDate,
        due_date: dueDate,
        items: items.filter(i => i.description),
        notes,
        tax_rate: taxRate,
        discount_amount: discountAmount,
        discount_type: discountType
      };

      await api.put(`/invoices/${editingInvoice.id}`, invoiceData);
      toast.success('Invoice updated successfully');
      setShowCreateForm(false);
      setEditingInvoice(null);
      resetForm();
      fetchData();
      setActiveTab('list');
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error('Failed to update invoice');
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success(`Invoice status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCancelEdit = () => {
    setEditingInvoice(null);
    setShowCreateForm(false);
    resetForm();
  };

  const handleSendEmail = (invoice, clientEmail) => {
    if (!clientEmail) {
      toast.error('Client email is required to send invoice');
      return;
    }
    
    // Get company name and details from settings
    const companyName = settings?.company_name || 'Our Company';
    const companyEmail = settings?.company_email || '';
    const companyPhone = settings?.company_phone || '';
    const invoiceNumber = invoice.invoice_number;
    const total = invoice.total?.toFixed(2) || '0.00';
    const subtotal = invoice.subtotal?.toFixed(2) || '0.00';
    const dueDate = invoice.due_date;
    const issueDate = invoice.issue_date;
    
    // Build items list
    const itemsList = invoice.items?.map(item => 
      `  • ${item.description}: ${item.quantity} x $${item.unit_price?.toFixed(2)} = $${item.amount?.toFixed(2)}`
    ).join('\n') || '';
    
    // Compose email subject and body
    const subject = encodeURIComponent(`Invoice #${invoiceNumber} from ${companyName}`);
    const body = encodeURIComponent(
      `Dear ${invoice.client?.name || 'Customer'},\n\n` +
      `Please find below the details of Invoice #${invoiceNumber}.\n\n` +
      `═══════════════════════════════════\n` +
      `INVOICE DETAILS\n` +
      `═══════════════════════════════════\n\n` +
      `Invoice Number: ${invoiceNumber}\n` +
      `Issue Date: ${issueDate}\n` +
      `Due Date: ${dueDate}\n\n` +
      `ITEMS:\n${itemsList}\n\n` +
      `───────────────────────────────────\n` +
      `Subtotal: $${subtotal}\n` +
      (invoice.discount_amount > 0 ? `Discount: -$${invoice.discount_amount?.toFixed(2)}\n` : '') +
      (invoice.tax_amount > 0 ? `Tax (${invoice.tax_rate}%): $${invoice.tax_amount?.toFixed(2)}\n` : '') +
      `TOTAL DUE: $${total}\n` +
      `───────────────────────────────────\n\n` +
      `Please process the payment by ${dueDate}.\n\n` +
      `If you have any questions, please contact us:\n` +
      (companyEmail ? `Email: ${companyEmail}\n` : '') +
      (companyPhone ? `Phone: ${companyPhone}\n` : '') +
      `\nThank you for your business!\n\n` +
      `Best regards,\n${companyName}`
    );
    
    // Open default email client with mailto link
    const mailtoLink = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    
    toast.success('Email client opened');
  };

  const handleSendReminder = async (invoice) => {
    if (!invoice.client?.email) {
      toast.error('Client email is required');
      return;
    }
    
    try {
      const response = await api.post(`/invoices/${invoice.id}/send-reminder`);
      const data = response.data;
      
      const companyName = data.company_name || settings?.company_name || 'Our Company';
      const invoiceNumber = data.invoice_number;
      const total = data.total?.toFixed(2) || '0.00';
      const dueDate = data.due_date;
      const isOverdue = data.is_overdue;
      const daysOverdue = data.days_overdue;
      
      // Compose reminder email
      let subject, body;
      
      if (isOverdue) {
        subject = encodeURIComponent(`Overdue: Invoice #${invoiceNumber} - Payment Reminder`);
        body = encodeURIComponent(
          `Dear ${data.client_name},\n\n` +
          `This is a friendly reminder that Invoice #${invoiceNumber} for $${total} is now ${daysOverdue} day(s) overdue.\n\n` +
          `Original Due Date: ${dueDate}\n` +
          `Amount Due: $${total}\n\n` +
          `We kindly request that you process this payment at your earliest convenience.\n\n` +
          `If you have already made the payment, please disregard this reminder.\n\n` +
          `If you have any questions or concerns, please don't hesitate to contact us.\n\n` +
          `Best regards,\n${companyName}`
        );
      } else {
        subject = encodeURIComponent(`Payment Reminder: Invoice #${invoiceNumber}`);
        body = encodeURIComponent(
          `Dear ${data.client_name},\n\n` +
          `This is a friendly reminder about Invoice #${invoiceNumber} for $${total}.\n\n` +
          `Due Date: ${dueDate}\n` +
          `Amount Due: $${total}\n\n` +
          `Please process the payment by the due date to avoid any late fees.\n\n` +
          `If you have already made the payment, please disregard this reminder.\n\n` +
          `Best regards,\n${companyName}`
        );
      }
      
      // Open email client
      const mailtoLink = `mailto:${data.client_email}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
      
      toast.success('Reminder email opened');
    } catch (error) {
      console.error('Reminder error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create reminder');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    try {
      toast.info('Uploading logo...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/invoices/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSettingsForm({ ...settingsForm, logo_url: response.data.logo_url });
      toast.success('Logo uploaded');
      fetchData();
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // For mobile devices, open in new tab
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.open(url, '_blank');
      } else {
        // For desktop, download file
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('PDF ready!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setItems(newItems);
    
    if (field === 'description') {
      fetchWorkSuggestions(value);
    }
  };

  const applySuggestion = (index, suggestion) => {
    const newItems = [...items];
    newItems[index].description = suggestion.description;
    newItems[index].unit_price = suggestion.average_price;
    setItems(newItems);
    setWorkSuggestions([]);
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         inv.client?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const awaitingPayment = invoices.filter(inv => inv.status === 'unpaid' || inv.status === 'overdue');

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400',
      unpaid: 'bg-yellow-500/20 text-yellow-400',
      overdue: 'bg-red-500/20 text-red-400',
      draft: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-gray-500/20 text-gray-400'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const { subtotal, discount, tax, total } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight mb-2">Invoices</h1>
            <p className="text-lg text-muted-foreground">Create and manage your invoices</p>
          </div>
          <button
            onClick={() => { setShowCreateForm(true); setActiveTab('create'); }}
            className="mt-4 md:mt-0 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl font-medium flex items-center space-x-2 transition-colors"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Create Invoice</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="w-5 h-5 text-indigo-400" />
                <span className="text-sm text-muted-foreground">Total Invoices</span>
              </div>
              <p className="text-2xl font-mono font-bold">{stats.total_invoices}</p>
            </div>
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <span className="text-sm text-muted-foreground">Paid</span>
              </div>
              <p className="text-2xl font-mono font-bold text-green-400">${stats.paid_amount.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-sm text-muted-foreground">Unpaid</span>
              </div>
              <p className="text-2xl font-mono font-bold text-yellow-400">${stats.unpaid_amount.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-sm text-muted-foreground">Overdue</span>
              </div>
              <p className="text-2xl font-mono font-bold text-red-400">${stats.overdue_amount.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Invoice Limit Warning for Free Users */}
        {userSubscription !== 'active' && stats && (
          <div className={`mb-6 p-4 rounded-xl border ${
            stats.total_invoices >= FREE_INVOICE_LIMIT 
              ? 'bg-red-500/10 border-red-500/30' 
              : stats.total_invoices >= FREE_INVOICE_LIMIT - 5
                ? 'bg-yellow-500/10 border-yellow-500/30'
                : 'bg-indigo-500/10 border-indigo-500/30'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <FileText className={`w-5 h-5 ${
                  stats.total_invoices >= FREE_INVOICE_LIMIT 
                    ? 'text-red-400' 
                    : stats.total_invoices >= FREE_INVOICE_LIMIT - 5
                      ? 'text-yellow-400'
                      : 'text-indigo-400'
                }`} />
                <div>
                  <p className="font-medium">
                    {stats.total_invoices >= FREE_INVOICE_LIMIT 
                      ? 'Invoice limit reached!' 
                      : `Free Plan: ${stats.total_invoices} / ${FREE_INVOICE_LIMIT} invoices`
                    }
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {stats.total_invoices >= FREE_INVOICE_LIMIT 
                      ? 'Upgrade to Premium for unlimited invoices' 
                      : `${FREE_INVOICE_LIMIT - stats.total_invoices} invoices remaining`
                    }
                  </p>
                </div>
              </div>
              {stats.total_invoices >= FREE_INVOICE_LIMIT - 5 && (
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Upgrade to Premium
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white/5 rounded-xl w-fit">
          {[
            { id: 'list', label: 'All Invoices', icon: FileText },
            { id: 'clients', label: 'Clients', icon: Users, count: clients.length },
            { id: 'awaiting', label: 'Awaiting Payment', icon: Clock, count: awaitingPayment.length },
            { id: 'reports', label: 'Reports', icon: BarChart3 },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`text-white text-xs px-2 py-0.5 rounded-full ${tab.id === 'awaiting' ? 'bg-red-500' : 'bg-indigo-500'}`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'list' && (
          <div>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by invoice # or client..."
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all" className="bg-[#0f172a]">All Status</option>
                <option value="paid" className="bg-[#0f172a]">Paid</option>
                <option value="unpaid" className="bg-[#0f172a]">Unpaid</option>
                <option value="overdue" className="bg-[#0f172a]">Overdue</option>
              </select>
            </div>

            {/* Invoices List */}
            <div className="space-y-3">
              {filteredInvoices.length > 0 ? (
                filteredInvoices.map(invoice => (
                  <div key={invoice.id} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="font-mono font-bold text-indigo-400">{invoice.invoice_number}</span>
                          {getStatusBadge(invoice.status)}
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: TEMPLATES.find(t => t.id === invoice.template_id)?.color || '#4F46E5' }}
                            title={`Template: ${invoice.template_id}`}
                          />
                        </div>
                        <p className="text-white font-medium">{invoice.client?.name || 'Unknown Client'}</p>
                        <p className="text-sm text-muted-foreground">
                          Issued: {invoice.issue_date} • Due: {invoice.due_date}
                        </p>
                      </div>
                      <div className="mt-4 md:mt-0 flex items-center space-x-4">
                        <p className="text-2xl font-mono font-bold">${invoice.total.toFixed(2)}</p>
                        <div className="flex flex-wrap gap-2">
                          {/* Edit Button - always visible */}
                          <button
                            onClick={() => handleEditInvoice(invoice)}
                            className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
                            title="Edit Invoice"
                            data-testid={`edit-invoice-${invoice.id}`}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          
                          {/* Status dropdown for quick status change */}
                          <select
                            value={invoice.status}
                            onChange={(e) => handleUpdateStatus(invoice.id, e.target.value)}
                            className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-sm cursor-pointer hover:bg-white/20 transition-colors"
                            title="Change Status"
                            data-testid={`status-select-${invoice.id}`}
                          >
                            <option value="unpaid" className="bg-[#0f172a]">Unpaid</option>
                            <option value="paid" className="bg-[#0f172a]">Paid</option>
                            <option value="overdue" className="bg-[#0f172a]">Overdue</option>
                            <option value="draft" className="bg-[#0f172a]">Draft</option>
                            <option value="cancelled" className="bg-[#0f172a]">Cancelled</option>
                          </select>
                          
                          {invoice.status !== 'paid' && (
                            <>
                              <button
                                onClick={() => handleMarkPaid(invoice.id)}
                                className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                title="Mark as Paid"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleSendEmail(invoice, invoice.client?.email)}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                title="Send via Email"
                              >
                                <Send className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleSendReminder(invoice)}
                                className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                title="Send Payment Reminder"
                              >
                                <Bell className="w-5 h-5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                            className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">No invoices found</p>
                  <button
                    onClick={() => { setShowCreateForm(true); setActiveTab('create'); }}
                    className="mt-4 text-indigo-400 hover:text-indigo-300"
                  >
                    Create your first invoice
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'awaiting' && (
          <div>
            <h2 className="text-xl font-bold mb-4">Invoices Awaiting Payment</h2>
            <div className="space-y-3">
              {awaitingPayment.length > 0 ? (
                awaitingPayment.map(invoice => (
                  <div key={invoice.id} className={`p-4 border rounded-xl ${invoice.status === 'overdue' ? 'bg-red-500/10 border-red-500/30' : 'bg-white/5 border-white/10'}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="font-mono font-bold">{invoice.invoice_number}</span>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="font-medium">{invoice.client?.name}</p>
                        <p className="text-sm text-muted-foreground">Due: {invoice.due_date}</p>
                      </div>
                      <div className="mt-4 md:mt-0 flex items-center space-x-3">
                        <p className="text-2xl font-mono font-bold">${invoice.total.toFixed(2)}</p>
                        <button
                          onClick={() => handleEditInvoice(invoice)}
                          className="px-4 py-2 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded-lg hover:bg-indigo-500/30 transition-colors flex items-center space-x-2"
                          title="Edit Invoice"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => handleSendReminder(invoice)}
                          className="px-4 py-2 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/30 transition-colors flex items-center space-x-2"
                          title="Send Payment Reminder"
                        >
                          <Bell className="w-4 h-4" />
                          <span className="hidden sm:inline">Remind</span>
                        </button>
                        <button
                          onClick={() => handleMarkPaid(invoice.id)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Mark Paid</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-400" />
                  <p className="text-lg">All invoices are paid!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Manage Clients</h2>
              <button
                onClick={() => { setShowClientForm(true); setEditingClient(null); setClientForm({ name: '', email: '', address: '', city: '', state: '', zip_code: '', phone: '' }); }}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-2 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Client</span>
              </button>
            </div>
            
            <div className="space-y-3">
              {clients.length > 0 ? (
                clients.map(client => (
                  <div key={client.id} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <p className="text-white font-medium text-lg">{client.name}</p>
                        <div className="text-sm text-muted-foreground mt-1 space-y-1">
                          {client.email && <p>{client.email}</p>}
                          {client.address && (
                            <p>
                              {client.address}
                              {client.city && `, ${client.city}`}
                              {client.state && `, ${client.state}`}
                              {client.zip_code && ` ${client.zip_code}`}
                            </p>
                          )}
                          {client.phone && <p>{client.phone}</p>}
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingClient(client);
                            setClientForm({
                              name: client.name || '',
                              email: client.email || '',
                              address: client.address || '',
                              city: client.city || '',
                              state: client.state || '',
                              zip_code: client.zip_code || '',
                              phone: client.phone || ''
                            });
                            setShowClientForm(true);
                          }}
                          className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
                          title="Edit Client"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Delete Client"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">No clients yet</p>
                  <button
                    onClick={() => { setShowClientForm(true); setEditingClient(null); }}
                    className="mt-4 text-indigo-400 hover:text-indigo-300"
                  >
                    Add your first client
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Invoice Reports</h2>
            
            {/* Filters */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
              <h3 className="font-semibold mb-4 flex items-center">
                <Filter className="w-5 h-5 mr-2" />
                Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Start Date</label>
                  <input
                    type="date"
                    value={reportFilters.start_date}
                    onChange={(e) => setReportFilters({...reportFilters, start_date: e.target.value})}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">End Date</label>
                  <input
                    type="date"
                    value={reportFilters.end_date}
                    onChange={(e) => setReportFilters({...reportFilters, end_date: e.target.value})}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Client</label>
                  <select
                    value={reportFilters.client_id}
                    onChange={(e) => setReportFilters({...reportFilters, client_id: e.target.value})}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">All Clients</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Service</label>
                  <select
                    value={reportFilters.service}
                    onChange={(e) => setReportFilters({...reportFilters, service: e.target.value})}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">All Services</option>
                    {availableServices.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Status</label>
                  <select
                    value={reportFilters.status}
                    onChange={(e) => setReportFilters({...reportFilters, status: e.target.value})}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setReportFilters({start_date: '', end_date: '', client_id: '', service: '', status: 'all'})}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
                >
                  Clear Filters
                </button>
                <button
                  onClick={fetchReportData}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Report
                </button>
              </div>
            </div>

            {/* Report Loading */}
            {reportLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            )}

            {/* Report Data */}
            {reportData && !reportLoading && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-5 h-5 text-indigo-400" />
                      <span className="text-sm text-muted-foreground">Total Invoices</span>
                    </div>
                    <p className="text-2xl font-mono font-bold">{reportData.total_invoices}</p>
                  </div>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-muted-foreground">Total Amount</span>
                    </div>
                    <p className="text-2xl font-mono font-bold text-blue-400">${reportData.total_amount.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-muted-foreground">Paid</span>
                    </div>
                    <p className="text-2xl font-mono font-bold text-green-400">${reportData.paid_amount.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      <span className="text-sm text-muted-foreground">Unpaid</span>
                    </div>
                    <p className="text-2xl font-mono font-bold text-yellow-400">${reportData.unpaid_amount.toFixed(2)}</p>
                  </div>
                </div>

                {/* By Client */}
                {Object.keys(reportData.by_client).length > 0 && (
                  <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-indigo-400" />
                      By Client
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(reportData.by_client)
                        .sort((a, b) => b[1] - a[1])
                        .map(([client, amount]) => (
                        <div key={client} className="flex items-center justify-between">
                          <span>{client}</span>
                          <div className="flex items-center space-x-4">
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-indigo-500 rounded-full"
                                style={{ width: `${(amount / reportData.total_amount) * 100}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm w-24 text-right">${amount.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Service */}
                {Object.keys(reportData.by_service).length > 0 && (
                  <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-purple-400" />
                      By Service
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(reportData.by_service)
                        .sort((a, b) => b[1] - a[1])
                        .map(([service, amount]) => (
                        <div key={service} className="flex items-center justify-between">
                          <span className="truncate max-w-xs">{service}</span>
                          <div className="flex items-center space-x-4">
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-purple-500 rounded-full"
                                style={{ width: `${(amount / reportData.total_amount) * 100}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm w-24 text-right">${amount.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Month */}
                {Object.keys(reportData.by_month).length > 0 && (
                  <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2 text-green-400" />
                      By Month
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(reportData.by_month).map(([month, amount]) => {
                        const [year, monthNum] = month.split('-');
                        const monthName = new Date(year, parseInt(monthNum) - 1).toLocaleString('default', { month: 'short', year: 'numeric' });
                        return (
                          <div key={month} className="flex items-center justify-between">
                            <span>{monthName}</span>
                            <div className="flex items-center space-x-4">
                              <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full"
                                  style={{ width: `${(amount / Math.max(...Object.values(reportData.by_month))) * 100}%` }}
                                />
                              </div>
                              <span className="font-mono text-sm w-24 text-right">${amount.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Invoice List */}
                {reportData.invoices && reportData.invoices.length > 0 && (
                  <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                    <h3 className="font-semibold mb-4">Filtered Invoices ({reportData.invoices.length})</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-muted-foreground border-b border-white/10">
                            <th className="pb-3 pr-4">Invoice #</th>
                            <th className="pb-3 pr-4">Date</th>
                            <th className="pb-3 pr-4">Client</th>
                            <th className="pb-3 pr-4">Status</th>
                            <th className="pb-3 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.invoices.map(invoice => {
                            const client = clients.find(c => c.id === invoice.client_id);
                            return (
                              <tr key={invoice.id} className="border-b border-white/5">
                                <td className="py-3 pr-4 font-mono">{invoice.invoice_number}</td>
                                <td className="py-3 pr-4">{invoice.issue_date}</td>
                                <td className="py-3 pr-4">{client?.name || 'Unknown'}</td>
                                <td className="py-3 pr-4">
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    invoice.status === 'paid' 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {invoice.status}
                                  </span>
                                </td>
                                <td className="py-3 text-right font-mono">${invoice.total.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {!reportData && !reportLoading && (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Click "Generate Report" to see invoice analytics</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <h2 className="text-xl font-bold mb-6">Invoice Settings</h2>
            <div className="space-y-6 p-6 bg-white/5 border border-white/10 rounded-xl">
              <div>
                <h3 className="text-lg font-medium mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Logo Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm text-muted-foreground mb-2">Company Logo</label>
                    <div className="flex items-center space-x-4">
                      {settingsForm.logo_url ? (
                        <img 
                          src={settingsForm.logo_url} 
                          alt="Company Logo" 
                          className="w-20 h-20 object-contain bg-white rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-white/10 border border-dashed border-white/30 rounded-lg flex items-center justify-center">
                          <Upload className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <span className="px-4 py-2 bg-white/10 border border-white/10 rounded-lg hover:bg-white/20 transition-colors inline-flex items-center space-x-2">
                            <Upload className="w-4 h-4" />
                            <span>{settingsForm.logo_url ? 'Change Logo' : 'Upload Logo'}</span>
                          </span>
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm text-muted-foreground mb-2">Company Name</label>
                    <input
                      type="text"
                      value={settingsForm.company_name}
                      onChange={(e) => setSettingsForm({...settingsForm, company_name: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-muted-foreground mb-2">Address</label>
                    <input
                      type="text"
                      value={settingsForm.company_address}
                      onChange={(e) => setSettingsForm({...settingsForm, company_address: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">City</label>
                    <input
                      type="text"
                      value={settingsForm.company_city}
                      onChange={(e) => setSettingsForm({...settingsForm, company_city: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">State</label>
                      <input
                        type="text"
                        value={settingsForm.company_state}
                        onChange={(e) => setSettingsForm({...settingsForm, company_state: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">ZIP</label>
                      <input
                        type="text"
                        value={settingsForm.company_zip}
                        onChange={(e) => setSettingsForm({...settingsForm, company_zip: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Phone</label>
                    <input
                      type="tel"
                      value={settingsForm.company_phone}
                      onChange={(e) => setSettingsForm({...settingsForm, company_phone: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Email</label>
                    <input
                      type="email"
                      value={settingsForm.company_email}
                      onChange={(e) => setSettingsForm({...settingsForm, company_email: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Invoice Defaults</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Invoice Prefix</label>
                    <input
                      type="text"
                      value={settingsForm.invoice_prefix}
                      onChange={(e) => setSettingsForm({...settingsForm, invoice_prefix: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Default Tax Rate (%)</label>
                    <input
                      type="number"
                      value={settingsForm.default_tax_rate}
                      onChange={(e) => setSettingsForm({...settingsForm, default_tax_rate: parseFloat(e.target.value) || 0})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Payment Terms (days)</label>
                    <input
                      type="number"
                      value={settingsForm.default_payment_terms}
                      onChange={(e) => setSettingsForm({...settingsForm, default_payment_terms: parseInt(e.target.value) || 30})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-3 rounded-xl font-medium transition-colors"
              >
                Save Settings
              </button>
            </div>
          </div>
        )}

        {/* Create/Edit Invoice Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold">{editingInvoice ? `Edit Invoice #${editingInvoice.invoice_number}` : 'Create Invoice'}</h2>
                <button onClick={handleCancelEdit} className="text-muted-foreground hover:text-white">
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Template Selection - Grid of 10 templates */}
                <div>
                  <label className="block text-sm font-medium mb-3">Choose Template</label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {TEMPLATES.map(t => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplate(t.id)}
                        data-testid={`template-${t.id}`}
                        className={`p-3 rounded-xl border transition-all ${
                          selectedTemplate === t.id 
                            ? 'border-2 border-indigo-500 bg-indigo-500/10' 
                            : 'border-white/10 hover:border-white/30 bg-white/5'
                        }`}
                      >
                        <div 
                          className="w-full h-3 rounded-full mb-2"
                          style={{ backgroundColor: t.color }}
                        />
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">Client</label>
                  <div className="flex space-x-2">
                    <select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="" className="bg-[#0f172a]">Select a client...</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id} className="bg-[#0f172a]">{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowClientForm(true)}
                      className="px-4 py-3 bg-white/10 border border-white/10 rounded-xl hover:bg-white/20 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Issue Date</label>
                    <input
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Due Date</label>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium">Line Items</label>
                    <label className="flex items-center space-x-2 text-sm cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={smartPriceEnabled}
                        onChange={(e) => setSmartPriceEnabled(e.target.checked)}
                        className="rounded border-white/10 bg-white/5 text-indigo-500"
                      />
                      <Zap className={`w-4 h-4 ${smartPriceEnabled ? 'text-yellow-400' : 'text-muted-foreground'}`} />
                      <span className={smartPriceEnabled ? 'text-yellow-400' : 'text-muted-foreground'}>
                        Smart Price {smartPriceEnabled ? 'ON' : 'OFF'}
                      </span>
                    </label>
                  </div>
                  
                  {smartPriceEnabled && (
                    <p className="text-xs text-muted-foreground mb-3 bg-yellow-500/10 border border-yellow-500/20 p-2 rounded-lg">
                      <Zap className="w-3 h-3 inline mr-1 text-yellow-400" />
                      Start typing in Description to see suggestions with average prices from your history
                    </p>
                  )}
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div key={index} className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
                        {/* Description - Full width */}
                        <div className="relative">
                          <label className="block text-xs text-muted-foreground mb-1">Description / Service</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="e.g. Web Development, Consulting, Design..."
                            data-testid={`item-description-${index}`}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                          />
                          {workSuggestions.length > 0 && index === items.length - 1 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-[#1e293b] border border-white/10 rounded-xl overflow-hidden z-10">
                              {workSuggestions.map((s, i) => (
                                <button
                                  key={i}
                                  onClick={() => applySuggestion(index, s)}
                                  className="w-full px-4 py-3 text-left hover:bg-white/10 flex justify-between items-center"
                                >
                                  <span>{s.description}</span>
                                  <span className="text-green-400 font-mono">${s.average_price.toFixed(2)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Qty, Price, Amount - Row */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Qty</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              placeholder="1"
                              data-testid={`item-qty-${index}`}
                              className="w-full px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Price ($)</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              value={item.unit_price}
                              onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                              placeholder="0.00"
                              data-testid={`item-price-${index}`}
                              className="w-full px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Amount</label>
                            <div className="w-full px-3 py-3 bg-indigo-500/20 border border-indigo-500/30 rounded-xl text-indigo-300 text-center font-mono font-bold">
                              ${(item.quantity * item.unit_price).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Delete button */}
                        {items.length > 1 && (
                          <button
                            onClick={() => removeItem(index)}
                            className="w-full py-2 text-red-400 hover:bg-red-500/20 rounded-lg text-sm flex items-center justify-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Remove Item</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addItem}
                    className="mt-3 text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Line Item</span>
                  </button>
                </div>

                {/* Tax & Discount */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Tax Rate (%)</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Discount</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Type</label>
                    <select
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                      <option value="fixed" className="bg-[#0f172a]">$ Fixed</option>
                      <option value="percentage" className="bg-[#0f172a]">% Percentage</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium mb-2">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Payment terms, thank you note, etc."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Totals */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="space-y-2 text-right">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">${subtotal.toFixed(2)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount</span>
                        <span className="font-mono text-green-400">-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    {tax > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax ({taxRate}%)</span>
                        <span className="font-mono">${tax.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                      <span>Total</span>
                      <span className="font-mono">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex space-x-4">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 px-6 py-3 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingInvoice ? handleUpdateInvoice : handleCreateInvoice}
                  data-testid={editingInvoice ? "update-invoice-btn" : "create-invoice-btn"}
                  className="flex-1 px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors font-medium"
                >
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Client Modal */}
        {showClientForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold">{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
                <button onClick={() => { setShowClientForm(false); setEditingClient(null); }} className="text-muted-foreground hover:text-white">
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Address</label>
                  <input
                    type="text"
                    value={clientForm.address}
                    onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                      type="text"
                      value={clientForm.city}
                      onChange={(e) => setClientForm({...clientForm, city: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">State</label>
                    <input
                      type="text"
                      value={clientForm.state}
                      onChange={(e) => setClientForm({...clientForm, state: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">ZIP</label>
                    <input
                      type="text"
                      value={clientForm.zip_code}
                      onChange={(e) => setClientForm({...clientForm, zip_code: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex space-x-4">
                <button
                  onClick={() => { setShowClientForm(false); setEditingClient(null); }}
                  className="flex-1 px-6 py-3 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateClient}
                  className="flex-1 px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors font-medium"
                >
                  {editingClient ? 'Update Client' : 'Add Client'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
